import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X, TriangleAlert } from "lucide-react";

/**
 * VenturaZ "Not a Stock Exchange" declaration popup.
 * Renders on public routes ~3s after page load, once per user (via
 * localStorage). Regulatory-mandated informed-consent style modal.
 */
const STORAGE_KEY = "venturaz_exchange_ack_v1";
const DELAY_MS = 3200;

export default function ExchangeDeclaration() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch (e) { /* private mode */ }
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const acknowledge = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-navy/60 backdrop-blur-sm p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exchange-decl-title"
      data-testid="exchange-declaration-modal"
    >
      <div className="relative w-full max-w-lg bg-white border border-gold/40 rounded-lg shadow-2xl overflow-hidden">
        <button
          onClick={acknowledge}
          className="absolute top-3 right-3 text-ink-muted hover:text-ink transition"
          aria-label="Close"
          data-testid="exchange-declaration-close"
        >
          <X size={16} />
        </button>
        <div className="p-6">
          <div className="flex items-center gap-2 text-amber-700 mb-3">
            <TriangleAlert size={14} />
            <div className="overline">IMPORTANT REGULATORY DECLARATION</div>
          </div>
          <h2 id="exchange-decl-title" className="font-heading text-ink text-xl mb-2">
            VenturaZ is not a stock exchange.
          </h2>
          <div className="text-sm text-ink-soft space-y-2">
            <p>
              VenturaZ is a <strong>Private Market Intelligence</strong>, diligence
              and capital-access platform. Access is permission-based and outcomes are
              decided independently by investors and founders.
            </p>
            <p>
              VenturaZ is <strong>NOT</strong> a recognised or unrecognised stock exchange,
              an alternative trading system, a public securities marketplace, a crowdfunding
              platform, an AIF, a portfolio manager, or a SEBI-registered investment adviser.
              Nothing on this platform constitutes an offer, a solicitation, or a
              recommendation to buy, sell or hold securities.
            </p>
            <p>
              All investors must conduct independent due diligence and consult qualified
              legal, tax and financial professionals before committing capital. Please
              also read our{" "}
              <Link
              to="/cms/compliance-declarations"
              className="text-gold underline decoration-gold/40 hover:decoration-gold"
              onClick={acknowledge}
              data-testid="exchange-declaration-compliance-link"
            >
              Compliance Declarations
            </Link>
            {" "}and{" "}
            <Link
              to="/cms/risk-disclosure"
              className="text-gold underline decoration-gold/40 hover:decoration-gold"
              onClick={acknowledge}
              data-testid="exchange-declaration-risk-link"
            >
              Risk Disclosure
            </Link>.
            </p>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 justify-end">
            <Link
              to="/cms/compliance-declarations"
              className="text-xs font-mono uppercase tracking-wider text-ink-muted hover:text-gold transition text-center px-3 py-2"
              onClick={acknowledge}
              data-testid="exchange-declaration-read-more"
            >
              Read Full Declarations
            </Link>
            <button
              onClick={acknowledge}
              className="inline-flex items-center gap-2 bg-navy text-cream px-4 py-2 rounded font-mono uppercase text-xs tracking-wider hover:bg-navy/90 transition"
              data-testid="exchange-declaration-ack"
            >
              <ShieldCheck size={12} /> I understand & acknowledge
            </button>
          </div>
        </div>
        <div className="bg-navy/[0.03] border-t border-navy/10 px-6 py-2 text-[10px] font-mono text-ink-muted uppercase tracking-wider">
          Operated by Ultiwise Ventures Private Limited · Noida · Powered by CorpZo
        </div>
      </div>
    </div>
  );
}
